import React, { useState, useEffect } from "react";
import {
    Table,
    TableBody,
    TableContainer,
    TableHead,
    Dialog,
    DialogContent,
    TableCell,
    TableRow,
    Box,
    CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomButton from "../buttons/CustomButton";
import { styled } from "@mui/system";
import styles from "./popup.module.css";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    "&.MuiTableCell-head": {
        fontFamily: "Unilever Shilling",
        backgroundColor: "#2036C7",
        color: "#fff",
        textAlign: "center",
        padding: "8px",
        fontSize: "12px",
        fontWeight: 400,
        border: "1px solid #EAECF0",
    },
    "&.MuiTableCell-body": {
        fontFamily: "Unilever Shilling",
        fontSize: 12,
        fontWeight: 400,
        padding: "8px",
        color: "#2B2B2B",
        border: "1px solid #EAECF0",
        textAlign: "center",
        verticalAlign: 'top'
    },
}));

const StyledTableRow = styled(TableRow)({});

const ActionSummaryPopup = ({ type, actionHistory, open, onClose, loading }) => {
    const [popupData, setPopupData] = useState([]);
    const [columns, setColumns] = useState([]);

    useEffect(() => {
        if (actionHistory && actionHistory.data) {
            setPopupData(actionHistory.data);

            if (actionHistory.columns && actionHistory.columns.length > 0) {
                if (typeof actionHistory.columns[0] === 'object') {
                    setColumns(actionHistory.columns);
                } else {
                    const columnObjects = actionHistory.columns.map(col => ({
                        key: col,
                        display_name: col
                    }));
                    setColumns(columnObjects);
                }
            }
        } else {
            // Reset when no data
            setPopupData([]);
            setColumns([]);
        }
    }, [actionHistory]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString();
        } catch {
            return dateString;
        }
    }

    const renderRecommendationTitles = (recommendations) => {
        if (!recommendations) return '-';

        if (Array.isArray(recommendations)) {
            if (recommendations.length === 0) return '-';

            const titles = recommendations.map(item => {
                if (item && typeof item === 'object' && item.Title) {
                    return item.Title;
                }
                return String(item);
            });

            return titles.join(', ');
        }
        return recommendations;
    };


    const formatValue = (value, columnKey) => {
        if (columnKey.includes('QtyAtRisk')) {
            return value.toFixed(1);
        }

        if (value === null || value === undefined || value === '') {
            return '-';
        }

        // Handle recommendation columns
        if (columnKey === 'SelectedRecommendationDetails' || columnKey === 'RejectedRecommendationDetails') {
            return renderRecommendationTitles(value);
        }

        // Handle date fields
        if ((columnKey.includes('Date') || columnKey.includes('date')) && type !== 'Contract Alert') {
            return formatDate(value);
        }

        return value;
    };


    // Reset data when popup closes
    useEffect(() => {
        if (!open) {
            setPopupData([]);
            setColumns([]);
        }
    }, [open]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    minHeight: '80vh',
                    maxHeight: '90vh',
                    width: '95%',
                    maxWidth: 'none'
                }
            }}
        >
            <Box className={styles.historyHeader}>
                <div className={styles.stickyTitle}>
                    <span className={styles.historyTitle}>
                        Action History - {type}
                    </span>
                    <CloseIcon className={styles.closeIcon} onClick={onClose} />
                </div>
            </Box>

            <DialogContent sx={{ padding: '10px', height: '100%' }}>
                {loading ? (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        py: 4,
                        justifyContent: 'center',
                        height: '200px'
                    }}>
                        <CircularProgress />
                        <span>Loading action history...</span>
                    </Box>
                ) : (
                    <TableContainer className={styles.tableContainer}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <StyledTableRow>
                                    {columns.length > 0 ? (
                                        columns.map((col, index) => (
                                            <StyledTableCell key={index}>
                                                {col.display_name || col.key || col}
                                            </StyledTableCell>
                                        ))
                                    ) : (
                                        <StyledTableCell>
                                            No columns available...
                                        </StyledTableCell>
                                    )}
                                </StyledTableRow>
                            </TableHead>
                            <TableBody>
                                {popupData.length > 0 ? (
                                    popupData.map((row, i) => (
                                        <StyledTableRow key={i}>
                                            {columns.map((col, index) => {
                                                return (
                                                    <StyledTableCell key={index}>
                                                        {formatValue(row[col.key], col.key)}
                                                    </StyledTableCell>
                                                )
                                            })}
                                        </StyledTableRow>
                                    ))
                                ) : (
                                    <StyledTableRow>
                                        <StyledTableCell
                                            colSpan={columns.length || 1}
                                            sx={{ whiteSpace: "nowrap", minWidth: "100px" }}
                                        >
                                            Data not available...
                                        </StyledTableCell>
                                    </StyledTableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>

            <div className={styles.dialogCloseBtn}>
                <CustomButton
                    label="Close"
                    type="back"
                    onClick={onClose}
                />
            </div>
        </Dialog>
    );
};

export default ActionSummaryPopup;